import { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { experiments } from "@/utils/experiment/manifest.ts";
import { useExperiment } from "@/utils/experiment/use-experiment.ts";
import { FeatureExposed } from "@/utils/feature-exposed";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { OptionalExceptFor } from "@/utils/typescript-utils.ts";

import { AiFocusVideo } from "./ai-focus-video.tsx";
import { HeroTabs } from "./tab-contents/tabs.tsx";
import {
    TEAM_DATA,
    TEAM_DATA_NEW_SCREENSHOTS,
    Team
} from "./tab-contents/team-data.tsx";
const DEFAULT_TEAM_DATA_ORIGINAL: ActiveTeamDataProps = {
    id: "default",
    videoLink:
        // eslint-disable-next-line local/no-non-orangelogic-images-eslint
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_FULL_LowBR.mp4",
    heroImage:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/32mac5076120jymg5y7rtwu4ru5g3s6l.webp",
    heroImageDimensions: {
        imageWidth: 1920,
        imageHeight: 1080
    }
};

const DEFAULT_TEAM_DATA_NEW_SCREENSHOTS: ActiveTeamDataProps = {
    ...DEFAULT_TEAM_DATA_ORIGINAL,
    videoLink:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/438r2lhmec7014pn861g30w8kx28ty08.mp4",
    heroImage:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/qgeohf56651o63ux63s54547n4v512b6/software-hero-image.webp",
    heroImageDimensions: {
        imageWidth: 1984,
        imageHeight: 1042
    }
};

export const DEFAULT_TEAM_DATA = DEFAULT_TEAM_DATA_ORIGINAL;

export type ActiveTeamDataProps = OptionalExceptFor<
    Team,
    "heroImage" | "heroImageDimensions"
>;

type HeroJiraCtaTemplateProps = PropsWithChildren<{
    gwp10965XflowRecommendation?: boolean;
}>;

export const HeroJiraCtaTemplate = ({
    children,
    gwp10965XflowRecommendation: gwp10965XflowRecommendationProp
}: HeroJiraCtaTemplateProps) => {
    const showGwp9758Test = checkFeatureFlagGate(
        featureFlags.gwp9758LoggedOutTest
    );
    const xflowRecommendationExperiment = useExperiment(
        experiments.gwp10965XflowRecommendation2NbpDriven
    );
    const gwp10965XflowRecommendationFromExperiment =
        xflowRecommendationExperiment.type === "enrolled" &&
        xflowRecommendationExperiment.value === true;
    const gwp10965XflowRecommendation =
        gwp10965XflowRecommendationProp ??
        gwp10965XflowRecommendationFromExperiment;
    const useNewScreenshots = checkFeatureFlagGate(
        featureFlags.gwp9214HomepageScreenshots
    );

    const teamData = useNewScreenshots ? TEAM_DATA_NEW_SCREENSHOTS : TEAM_DATA;
    const defaultTeamData = useNewScreenshots
        ? DEFAULT_TEAM_DATA_NEW_SCREENSHOTS
        : DEFAULT_TEAM_DATA_ORIGINAL;

    return (
        <UFOSegment name="homepage-v2-hero">
            <section id="hero-jira-cta" css={jiraHeroStyles}>
                <>
                    {children}

                    {showGwp9758Test || gwp10965XflowRecommendation ? (
                        <AiFocusVideo />
                    ) : (
                        <FeatureExposed
                            experimentKey="gwp-1705-homepage-hero"
                            group="Test"
                        >
                            <HeroTabs
                                teamData={teamData}
                                defaultTeamData={defaultTeamData}
                            />
                        </FeatureExposed>
                    )}
                </>
            </section>
        </UFOSegment>
    );
};

const jiraHeroStyles = css({
    overflow: "hidden"
});

HeroJiraCtaTemplate.displayName = "HeroJiraCtaTemplate";
